// ---------------------------------------------------------------------------
// Service Cost Allocations — AG-027 Phase 1 (schema only)
// ---------------------------------------------------------------------------
// Story AG-027: "Agency analytics shows per-service profitability, retention,
// churn risk, capacity, and valuation recommendations."
//
// Per docs/vision_execution/openclaw/AG-027-DECISION-PACKET.md the
// profitability rollup needs a cost-of-delivery row keyed by
// (tenant, service, effective period) so the agency analytics service can
// compute `marginBps = (revenue - cost) / revenue` per service across the
// agency's managed sub-accounts.
//
// Phase 1 scope (this file): persistence schema only. The rollup service,
// the `GET /agency/analytics/profitability/:orgId` route, and the
// other four AG-027 surfaces (retention, churn, capacity, valuation) land in
// later phases governed by the decision packet.
//
// Design rules locked here:
// - One row per (tenant_id, service_id, effective_from). New allocations
//   supersede old ones; the active row is the latest `effective_from <= now()`
//   with `effective_to IS NULL OR effective_to > now()`.
// - Non-overlapping windows enforced by an EXCLUDE constraint shipped in
//   migration 0104 (`service_cost_allocations_no_overlap_excl`, GiST + &&).
//   Two rows with overlapping [effective_from, effective_to) ranges for the
//   same (tenant_id, service_id) are rejected at INSERT/UPDATE, preventing
//   the profitability rollup from picking up multiple active rows for one
//   service. Drizzle does not natively model EXCLUDE constraints, so the
//   constraint lives in raw SQL — the schema test asserts on the migration
//   text to keep the contract reviewable.
// - Currency stored explicitly so cross-tenant agency rollups can refuse
//   to aggregate mixed-currency rows without an FX adapter.
// - All cost components are integer cents (no float drift). Total cost
//   is `fixed_cost_cents + variable_cost_per_unit_cents * units`.
// - GDPR posture: tenant-scoped business data; cascade by tenant_id.
// ---------------------------------------------------------------------------

import {
  check,
  index,
  integer,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

/**
 * Closed allocation method vocabulary. The agency analytics rollup picks
 * the method when surfacing margin so the operator can see whether the
 * cost was a flat allocation, a per-unit variable, or a hybrid.
 */
export const SERVICE_COST_ALLOCATION_METHODS = [
  "flat",
  "per_unit",
  "hybrid",
] as const;

export type ServiceCostAllocationMethod =
  (typeof SERVICE_COST_ALLOCATION_METHODS)[number];

export const serviceCostAllocations = pgTable(
  "service_cost_allocations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    /**
     * Service identifier — text rather than uuid so this row can carry cost
     * allocations for service offerings that live in different table shapes
     * across product verticals (booking services, course offerings, ...).
     */
    serviceId: text("service_id").notNull(),
    /**
     * ISO-4217 currency code. Aggregation refuses to mix currencies; the
     * rollup either picks a tenant-default and excludes the rest or fails
     * closed with a structured error.
     */
    currency: text("currency").notNull(),
    /** Allocation method — see SERVICE_COST_ALLOCATION_METHODS. */
    method: text("method").notNull(),
    /** Flat per-period cost component in integer cents (>= 0). */
    fixedCostCents: integer("fixed_cost_cents").notNull().default(0),
    /** Per-unit variable cost component in integer cents (>= 0). */
    variableCostPerUnitCents: integer("variable_cost_per_unit_cents")
      .notNull()
      .default(0),
    /** Unit label for variable cost (e.g. "appointment", "hour"). */
    unitLabel: text("unit_label"),
    /** Allocation period start, inclusive. */
    effectiveFrom: timestamp("effective_from", tz).notNull(),
    /**
     * Allocation period end, exclusive. NULL = open-ended (current row).
     * The active row at any timestamp t is the row with
     *   effective_from <= t AND (effective_to IS NULL OR effective_to > t).
     */
    effectiveTo: timestamp("effective_to", tz),
    /**
     * Operator-supplied rationale (e.g. "Q3 vendor renegotiation"). Surfaced
     * in the profitability dashboard tooltip so the margin number is
     * auditable.
     */
    note: text("note"),
    createdBy: uuid("created_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    /**
     * Idempotency on the row identity. Same tenant/service/effective_from
     * combination is a single allocation row — an "update" of the same
     * effective period is an UPDATE, not an INSERT.
     */
    uniqueIndex("service_cost_allocations_tenant_svc_from_uq").on(
      table.tenantId,
      table.serviceId,
      table.effectiveFrom,
    ),
    /** Active-row lookup index for the profitability rollup. */
    index("service_cost_allocations_tenant_svc_active_idx").on(
      table.tenantId,
      table.serviceId,
      table.effectiveTo,
    ),
    /** Per-tenant audit / list ordering. */
    index("service_cost_allocations_tenant_from_idx").on(
      table.tenantId,
      table.effectiveFrom,
    ),
    check(
      "service_cost_allocations_fixed_nonneg_ck",
      sql`${table.fixedCostCents} >= 0`,
    ),
    check(
      "service_cost_allocations_variable_nonneg_ck",
      sql`${table.variableCostPerUnitCents} >= 0`,
    ),
    check(
      "service_cost_allocations_period_ck",
      sql`${table.effectiveTo} IS NULL OR ${table.effectiveTo} > ${table.effectiveFrom}`,
    ),
    check(
      "service_cost_allocations_method_ck",
      sql`${table.method} IN ('flat','per_unit','hybrid')`,
    ),
  ],
);
